/**
 * What a recorded customer answer did, from the return the server actually attempted (MAR-175).
 * The answer is always RECORDED once applied; only `executed` means it reached the customer, so
 * nothing else may say "sent" — a return queued for later, a reply the agent judged unneeded, an
 * erased customer, a return withheld by governance (`suppressed`: a hold is the system working,
 * never a failure — and its reason is any BLOCK_REASONS value, a suppression list or a fail-closed
 * read as often as a sending limit, so the copy names none of them), or a resumed apply whose earlier return can't be proven
 * (`not_refired`) each say what is known. Only the default — a genuine failure or an unrecognised
 * status — is failure copy.
 *
 * Shared by the Ask proposal receipt, the Inbox and the Ledger receipt drawer, which all report the
 * same return.
 */
export function answerOutcome(returnStatus: string | undefined): string {
  switch (returnStatus) {
    case 'executed': return 'answer sent'
    // The gateway accepted it and scheduled the send for the later time the agent chose — not yet sent.
    case 'scheduled': return 'answer recorded — queued to send'
    // The agent decided not to reply (the customer already bought, the answer is moot) — a
    // judgment recorded on the decision, not a failure.
    case 'skipped': return "answer recorded — she judged a reply wasn't needed, so nothing was sent"
    case 'customer_erased': return 'answer recorded — the customer asked to be forgotten, so nothing was sent'
    // A governance hold (the gateway withheld the send; its reason is ANY BLOCK_REASONS value —
    // a sending limit, a suppression list, a fail-closed read). The receipt carries only the
    // status, not the reason, so the copy is neutral: true for every one of them. Not a failure.
    case 'suppressed': return 'answer recorded — withheld by governance, not sent'
    // A resumed apply: an earlier attempt answered and fired the return, so this one did not fire
    // it again. That return is its own Ledger line — her reply to the customer — which is where
    // whether it reached them is recorded.
    case 'not_refired': return 'answer recorded by an earlier attempt — her reply to the customer is its own line in the Ledger'
    case undefined: return 'answer recorded'
    default: return "answer recorded — it couldn't be delivered"
  }
}

/**
 * What a DECLINED question did (MAR-175). A decline is not silence: the customer is owed an honest
 * close ("couldn't get a firm answer"), and the server fires it through the same return path as an
 * answer — so the copy follows the same rule. Only `executed` means the close reached the customer;
 * every other status says what is known, and only the default is failure copy.
 */
export function closeOutcome(returnStatus: string | undefined): string {
  switch (returnStatus) {
    case 'executed': return 'she closed it honestly — no guess, no promise'
    case 'scheduled': return 'closed — her honest reply is queued to send'
    case 'skipped': return "closed — she judged a reply wasn't needed, so nothing was sent"
    case 'customer_erased': return 'closed — the customer asked to be forgotten, so nothing was sent'
    // A hold is the system working, not a failure; neutral for every BLOCK_REASONS value.
    case 'suppressed': return 'closed — withheld by governance, not sent'
    case undefined: return 'closed'
    default: return "closed — her reply couldn't be delivered"
  }
}
